<script setup lang="ts">
import { supabase } from '@/composables/supabaseClient'
import numberToCurrency from '@/helpers/numberToCurrency'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed, ref } from 'vue'
import BaseButton from './BaseButton.vue'
import type { DataTableColumn } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'
import BaseModal from './BaseModal.vue'
import DatabaseItemDivisions from './DatabaseItemDivisions.vue'

const databaseStore = useDatabaseStore()

const rows = computed(() => {
  const defaultCostSource = databaseStore.costSources?.find(
    (costSource) => costSource.name == 'Default',
  )
  return (
    databaseStore.items?.map((item) => {
      const defaultCosts = databaseStore.itemCosts?.find(
        (cost) => cost.cost_source_id == defaultCostSource?.id && cost.item_id == item.id,
      )
      const unitCost = defaultCosts
        ? (defaultCosts.material_cost ?? 0) +
          (defaultCosts.labor_cost ?? 0) +
          (defaultCosts.equipment_cost ?? 0) +
          (defaultCosts.subcontractor_cost ?? 0) +
          (defaultCosts.other_cost ?? 0)
        : 0
      const division = databaseStore.itemDivisions?.find((d) => d.id === item.item_division_id)
      const parentDivision = databaseStore.itemDivisions?.find(
        (d) => d.id === division?.parent_division_id,
      )
      return {
        item_parent_division_id: parentDivision?.id,
        unit_cost: numberToCurrency(unitCost),
        ...item,
      }
    }) ?? []
  )
})
type Row = (typeof rows.value)[number]

const columns = computed<DataTableColumn<Row>[]>(() => {
  return [
    {
      name: 'Division',
      columnDataKey: 'item_parent_division_id',
      columnType: 'select',
      selectOptions: () => {
        const options = databaseStore.itemDivisions
          ?.filter((div) => !div.parent_division_id)
          ?.map((div) => {
            return {
              name: `${div?.code} ${div?.name}`,
              value: div.id,
            }
          })
        return options ?? []
      },
      textDisplayFunction: (value) => {
        const division = databaseStore.itemDivisions?.find((i) => i.id === value)
        return `${division?.code} ${division?.name}`
      },
      defaultWidth: 150,
      required: true,
      importConversion: (value) => {
        // Value is the division code
        const division = databaseStore.itemDivisions?.find((d) => d.code === value)
        if (!division) throw Error(`Unable to find division code ${value} for column division`)
        return division.id
      },
    },
    {
      name: 'Subdivision',
      columnDataKey: 'item_division_id',
      columnType: 'select',
      selectOptions: (row) => {
        const options = databaseStore.itemDivisions
          ?.filter(
            (div) =>
              div.parent_division_id && row.item_parent_division_id === div.parent_division_id,
          )
          ?.map((div) => {
            return {
              name: `${div?.code} ${div?.name}`,
              value: div.id,
            }
          })
        return options ?? []
      },
      textDisplayFunction: (value) => {
        const division = databaseStore.itemDivisions?.find((i) => i.id === value)
        return `${division?.code} ${division?.name}`
      },
      defaultWidth: 150,
      importConversion: (value) => {
        const division = databaseStore.itemDivisions?.find((d) => d.code === value)
        if (!division)
          throw toastThrowError(`Unable to find subdivision code ${value} for column Subdivision`)
        return division.id
      },
    },
    {
      name: 'Item Code',
      columnDataKey: 'code',
      defaultWidth: 75,
    },
    {
      name: 'Accounting Code',
      columnDataKey: 'accounting_code',
      defaultWidth: 75,
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      defaultWidth: 300,
    },
    {
      name: 'UOM',
      columnDataKey: 'uom_id',
      columnType: 'select',
      selectOptions: () => {
        const options = databaseStore.unitsOfMeasure?.map((uom) => {
          return {
            name: uom.code,
            value: uom.id,
          }
        })
        return options ?? []
      },
      textDisplayFunction: (value) =>
        databaseStore.unitsOfMeasure?.find((u) => u.id === value)?.code ?? '',
      defaultWidth: 75,
      importConversion: (value) => {
        const uom = databaseStore.unitsOfMeasure?.find((uom) => uom.code === value)
        if (!uom) throw toastThrowError(`Unable to find import value ${value} for UOM column`)
        return uom.id
      },
    },
    {
      name: 'Unit Cost',
      columnDataKey: 'unit_cost',
      columnType: 'disabled',
      defaultWidth: 75,
    },
  ]
})

const groups = [
  {
    name: 'None',
    fn: undefined,
  },
  {
    name: 'Division',
    fn: (row: Row) => {
      const subdivision = databaseStore.itemDivisions?.find((d) => d.id === row.item_division_id)
      const division = databaseStore.itemDivisions?.find(
        (d) => d.id === subdivision?.parent_division_id,
      )
      return `${division?.code} ${division?.name}`
    },
  },
  {
    name: 'Subdivision',
    fn: (row: Row) => {
      const subdivision = databaseStore.itemDivisions?.find((d) => d.id === row.item_division_id)
      const division = databaseStore.itemDivisions?.find(
        (d) => d.id === subdivision?.parent_division_id,
      )
      return `${division?.code}.${subdivision?.code} ${subdivision?.name}`
    },
  },
]

const showNewDivisionInputModal = ref(false)

async function createItem(row: Row) {
  const result = await supabase
    .from('item')
    .insert({
      id: uuidv7(),
      item_division_id: row.item_division_id,
      code: row.code,
      accounting_code: row.accounting_code,
      name: row.name,
      uom_id: row.uom_id,
      labor_type_id: row.labor_type_id,
      labor_productivity_per_hr: row.labor_productivity_per_hr,
      equipment_type_id: row.equipment_type_id,
      equipment_productivity_per_hr: row.equipment_productivity_per_hr,
      is_archived: false,
    })
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to add item', result.error)
  databaseStore.items?.push(result.data)
}
</script>
<template>
  <div class="flex h-full w-full flex-col gap-3 p-3">
    <BaseDataTable
      :rows="rows"
      :columns="columns"
      :groups="groups"
      row-key="id"
      table-name="test-table"
      show-new-row
      show-row-actions
      allow-import
      @add-new-row="createItem"
      sortable-columns
    >
      <template #tableActions>
        <div>
          <BaseButton @click="showNewDivisionInputModal = true">Divisions</BaseButton>
        </div>
      </template>
      <template #rowActions="props">
        <button
          class="cursor-pointer rounded p-1 hover:bg-gray-200"
          @click="router.push({ name: 'DatabaseItem', params: { itemId: props.row.id } })"
        >
          <svg
            class="size-4 text-gray-800"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10.779 17.779 4.36 19.918 6.5 13.5m4.279 4.279 8.364-8.643a3.027 3.027 0 0 0-2.14-5.165 3.03 3.03 0 0 0-2.14.886L6.5 13.5m4.279 4.279L6.499 13.5m2.14 2.14 6.213-6.504M12.75 7.04 17 11.28"
            />
          </svg>
        </button>
      </template>
    </BaseDataTable>
    <BaseModal title="Divisions" v-model:show-modal="showNewDivisionInputModal">
      <div class="flex h-full w-full flex-col overflow-auto">
        <DatabaseItemDivisions />
      </div>
    </BaseModal>
  </div>
</template>
